import {
  DIMENSION_POSITION_ATTRIBUTE,
  POSITION_COMPLEMENTS,
  POSITION_SUBSTITUTES,
  RELATED_DIMENSION,
} from './dimensions';
import {
  getPrimaryAxisPosition,
  getCrossAxisPosition,
} from './getAxisPositions';
import { intersectBoundingBoxes, getVisibleFit } from './utils';

/**
 * Given a target position and the popover's surrounding context,
 * returns either an object with { top, left } screen coordinates or
 * null if it's not possible to show the content in the
 * target position. It also returns how well the popover fits in a range of
 * 0.0 -> 1.0.
 */

const getScreenCoordinates = ({
  align,
  anchorBoundingBox,
  arrowConfig,
  buffer = 0,
  containerBoundingBox,
  offset = 0,
  popoverBoundingBox,
  position,
  windowBoundingBox,
}) => {
  /**
   * The goal is to find the best way to align the popover content
   * on the given side of the anchor element. The popover prefers
   * centering on the anchor but can shift along the cross-axis as needed.
   *
   * We return the top/left coordinates that best fit the popover inside
   * the given boundaries, and also return the `fit` value which indicates
   * what percentage of the popover is within the bounds.
   *
   * e.g. finding a location when position=top
   * the preferred location is directly over the anchor
   *
   *        +----------------------+
   *        |       popover        |
   *        +----------------------+
   *                   v
   *            +--------------+
   *            |    anchor    |
   *            +--------------+
   *
   * but if anchor doesn't have much (or any) room on its ride side
   * the popover will shift to the left
   *
   *    +----------------------+
   *    |       popover        |
   *    +----------------------+
   *                   v
   *            +--------------+
   *            |    anchor    |
   *            +--------------+
   *
   */

  const crossAxisFirstSide = POSITION_SUBSTITUTES[position]; // top -> left
  const crossAxisSecondSide = POSITION_COMPLEMENTS[crossAxisFirstSide]; // left -> right
  const crossAxisDimension = RELATED_DIMENSION[crossAxisFirstSide]; // left -> width

  const { crossAxisPosition, crossAxisArrowPosition } = getCrossAxisPosition({
    align,
    anchorBoundingBox,
    arrowConfig,
    buffer,
    containerBoundingBox,
    crossAxisFirstSide,
    crossAxisSecondSide,
    crossAxisDimension,
    offset,
    popoverBoundingBox,
    position,
    windowBoundingBox,
  });

  const primaryAxisDimension = RELATED_DIMENSION[position]; // top -> height
  const primaryAxisPositionName =
    DIMENSION_POSITION_ATTRIBUTE[primaryAxisDimension];

  const { primaryAxisPosition, primaryAxisArrowPosition } =
    getPrimaryAxisPosition({
      anchorBoundingBox,
      arrowConfig,
      offset,
      popoverBoundingBox,
      position,
    });

  const popoverPlacement = {
    [crossAxisFirstSide]: crossAxisPosition,
    [primaryAxisPositionName]: primaryAxisPosition,
  };

  // Calculate the fit of the popover in this location
  // fit is in the range 0.0 -> 1.0 and is the percentage of the popover
  // which is visible in that location

  const combinedBoundingBox = intersectBoundingBoxes(
    windowBoundingBox,
    containerBoundingBox
  );

  // Shrink the visible bounding box by a buffer to compute a fit value
  combinedBoundingBox.top += buffer;
  combinedBoundingBox.right -= buffer;
  combinedBoundingBox.bottom -= buffer;
  combinedBoundingBox.left += buffer;

  const fit = getVisibleFit(
    {
      bottom: popoverPlacement.top + popoverBoundingBox.height,
      height: popoverBoundingBox.height,
      left: popoverPlacement.left,
      right: popoverPlacement.left + popoverBoundingBox.width,
      top: popoverPlacement.top,
      width: popoverBoundingBox.width,
    },
    combinedBoundingBox
  );

  const arrow = arrowConfig
    ? {
        [crossAxisFirstSide]:
          crossAxisArrowPosition - popoverPlacement[crossAxisFirstSide],
        [primaryAxisPositionName]: primaryAxisArrowPosition,
      }
    : undefined;

  return {
    arrow,
    fit,
    left: popoverPlacement.left,
    top: popoverPlacement.top,
  };
};

export default getScreenCoordinates;
